import type { GenericGraphProps } from "@similarweb/pro-logic-components/dist/graphs/types";
import { colorsPalettes, colorsSets } from "@similarweb/styles";

import type { ISearchAdsSpend } from "app/services/searchAdsSpend/types";

const { blue } = colorsPalettes;

interface ITransformPpcSpendDataOptions {
    isPercentage: boolean;
    isCompare?: boolean;
}

interface ISpendPoint {
    x: number;
    y: number | null;
}

interface ISpendSeries {
    id: string;
    name: string;
    data: ISpendPoint[];
    visible: boolean;
    color: string;
    total: number;
}

const averageOf = (points: ISpendPoint[]): number => {
    const valued = points.filter((point) => point.y !== null);
    return valued.length > 0 ? valued.reduce((sum, point) => sum + point.y, 0) / valued.length : 0;
};

/**
 * Shares are computed per month across the domains that have a value for it; a month with no value
 * for a domain stays null so the chart renders a gap rather than a zero.
 */
const toShares = (series: ISpendSeries[]): ISpendSeries[] => {
    const monthTotals = new Map<number, number>();
    for (const item of series) {
        for (const point of item.data) {
            if (point.y !== null) {
                monthTotals.set(point.x, (monthTotals.get(point.x) ?? 0) + point.y);
            }
        }
    }

    return series.map((item) => {
        const data = item.data.map((point) => {
            const monthTotal = monthTotals.get(point.x) ?? 0;
            if (point.y === null) {
                return point;
            }
            return { ...point, y: monthTotal === 0 ? 0 : point.y / monthTotal };
        });

        return { ...item, data, total: averageOf(data) };
    });
};

export function transformPpcSpendData(
    spend: ISearchAdsSpend,
    { isPercentage, isCompare }: ITransformPpcSpendDataOptions,
): GenericGraphProps {
    const colorsSet = colorsSets.c.toArray();

    const series: ISpendSeries[] = spend.series.map((domainSeries, index) => ({
        id: domainSeries.domain,
        name: domainSeries.domain,
        data: domainSeries.points.map((point) => ({
            x: new Date(point.date).getTime(),
            y: point.value,
        })),
        visible: true,
        color: isCompare ? colorsSet[index % colorsSet.length] : blue["400"],
        total: domainSeries.total ?? 0,
    }));

    // The chart series union from Highcharts does not describe these plain line series.
    return (isPercentage ? toShares(series) : series) as unknown as GenericGraphProps;
}
